import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, Table, Card, Badge } from 'react-bootstrap'
import { recipeAPI } from '../api/recipeAPI'
import { productionAPI } from '../api/productionAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import PickInventoryModal from '../components/PickInventoryModal'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import { useWarehouse } from '../contexts/WarehouseContext'
import CopyableText from '../components/CopyableText'
import QuantityDisplay from '../components/QuantityDisplay'
import { isSuperUnit, getPieceLabel } from '../components/QuantityPicker'

function RecipeExecuteScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { currentWarehouse } = useWarehouse()

    const [recipe, setRecipe] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [outputQuantity, setOutputQuantity] = useState(0)
    const [maxQuantity, setMaxQuantity] = useState(0)

    // Inventory picking state
    const [ingredientAllocations, setIngredientAllocations] = useState({})
    const [pickedTotals, setPickedTotals] = useState({})
    // { ingredientId: { inventoryId: quantity, ... }, ... }
    const [showPickModal, setShowPickModal] = useState(false)
    const [selectedIngredient, setSelectedIngredient] = useState(null)

    // Output location state
    const [warehouses, setWarehouses] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState('')
    const [locations, setLocations] = useState([])
    const [selectedLocation, setSelectedLocation] = useState('')
    const [loadingLocations, setLoadingLocations] = useState(false)

    // Output metadata state
    const [outputLot, setOutputLot] = useState('')
    const [outputBbDate, setOutputBbDate] = useState('')

    // Execution state
    const [startingProduction, setStartingProduction] = useState(false)
    const [successMessage, setSuccessMessage] = useState(null)
    const [submitError, setSubmitError] = useState(null)


    // Ingredient availability tracking
    const [ingredientAvailability, setIngredientAvailability] = useState({})

    useEffect(() => {
        fetchRecipe()
        fetchWarehouses()
    }, [id])

    // Fetch locations when warehouse changes
    useEffect(() => {
        if (selectedWarehouse) {
            fetchLocations(selectedWarehouse)
        } else {
            setLocations([])
            setSelectedLocation('')
        }
    }, [selectedWarehouse])

    // Set warehouse from user's current warehouse setting
    useEffect(() => {
        if (currentWarehouse && !selectedWarehouse) {
            const warehouseId = currentWarehouse._id || currentWarehouse.id
            setSelectedWarehouse(warehouseId.toString())
        }
    }, [currentWarehouse, selectedWarehouse])

    // Check if all required fields are filled and all ingredients are picked
    const isFormComplete = useMemo(() => {
        // Check required fields
        if (!selectedLocation) return false
        if (outputQuantity <= 0) return false

        // Check if recipe is loaded
        if (!recipe) return false

        // Check if all ingredients have allocations
        const ingredients = recipe.ingredients || []
        for (const ing of ingredients) {
            const allocs = ingredientAllocations[ing._id] || {}
            if (Object.keys(allocs).length === 0) return false
        }

        return true
    }, [selectedLocation, outputQuantity, recipe, ingredientAllocations])

    // Permission check - must be after all hooks
    if (!permissionsLoading && !hasPermission('production.manage')) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    You don&apos;t have permission to execute recipes. Required: <code>production.manage</code>
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes' + returnFilters)}>
                    Back to Recipes
                </Button>
            </Container>
        )
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            // Filter to only active warehouses
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocations = async (warehouseId) => {
        try {
            setLoadingLocations(true)
            const data = await locationAPI.getAll(warehouseId)
            // Filter to only active locations
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocations(activeLocations)

            // Auto-select location ending with "-production" (case-insensitive)
            const productionLoc = activeLocations.find(loc =>
                loc.name.toLowerCase().endsWith('-production')
            )
            if (productionLoc) {
                setSelectedLocation(productionLoc._id)
            } else {
                setSelectedLocation('')
            }
        } catch (error) {
            console.error('Error fetching locations:', error)
            setLocations([])
        } finally {
            setLoadingLocations(false)
        }
    }

    const fetchRecipe = async () => {
        try {
            setLoading(true)
            const data = await recipeAPI.getById(id)
            setRecipe(data)

            try {
                const { max_output } = await recipeAPI.getMaxOutput(id)
                setMaxQuantity(max_output)
                setOutputQuantity(Math.min(data.yield_quantity || 1, max_output))
            } catch (err) {
                console.error('Error fetching max output:', err)
                setMaxQuantity(0)
                setOutputQuantity(0)
            }

            // Fetch availability for each ingredient
            if (data.ingredients && data.ingredients.length > 0) {
                fetchIngredientAvailability(data.ingredients)
            }
        } catch (error) {
            console.error('Error fetching recipe:', error)
            setError('Failed to load recipe data')
        } finally {
            setLoading(false)
        }
    }

    const fetchIngredientAvailability = async (ingredients) => {
        const availability = {}
        for (const ingredient of ingredients) {
            try {
                const inventoryItems = await recipeAPI.getIngredientInventory(id, ingredient._id)
                const totalAvailable = (inventoryItems || []).reduce(
                    (sum, item) => sum + parseFloat(item.quantity || 0), 0
                )
                availability[ingredient._id] = totalAvailable
            } catch (err) {
                console.error(`Error fetching inventory for ingredient ${ingredient._id}:`, err)
                availability[ingredient._id] = 0
            }
        }
        setIngredientAvailability(availability)
    }

    const handleQuantityChange = (value) => {
        const numValue = parseFloat(value) || 0
        setOutputQuantity(Math.min(Math.max(1, numValue), maxQuantity))
    }

    // Calculate required quantity based on output quantity and yield
    const calculateRequiredQuantity = (ingredient) => {
        const outputQty = outputQuantity || 0
        const yieldQty = recipe?.yield_quantity || 1
        const ratio = outputQty / yieldQty
        const wasteFactor = 1 + (parseFloat(ingredient.waste_percentage) || 0) / 100
        return (parseFloat(ingredient.quantity) * wasteFactor * ratio).toFixed(4)
    }

    // Calculate required weight for weight-mode ingredients
    const calculateRequiredWeight = (ingredient) => {
        const outputQty = outputQuantity || 0
        const yieldQty = recipe?.yield_quantity || 1
        const ratio = outputQty / yieldQty
        const wasteFactor = 1 + (parseFloat(ingredient.waste_percentage) || 0) / 100
        return (parseFloat(ingredient.weight_value) * wasteFactor * ratio).toFixed(3)
    }

    const calculateTheoreticalQuantity = (ingredient) => {
        const outputQty = outputQuantity || 0
        const yieldQty = recipe?.yield_quantity || 1
        const ratio = outputQty / yieldQty
        return (parseFloat(ingredient.quantity) * ratio).toFixed(4)
    }

    const calculateTheoreticalWeight = (ingredient) => {
        const outputQty = outputQuantity || 0
        const yieldQty = recipe?.yield_quantity || 1
        const ratio = outputQty / yieldQty
        return (parseFloat(ingredient.weight_value) * ratio).toFixed(3)
    }

    // Open pick modal for an ingredient
    const openPickModal = (ingredient) => {
        setSelectedIngredient(ingredient)
        setShowPickModal(true)
    }

    // Handle allocation confirmation from modal
    const handleAllocationConfirm = (allocations, totalPicked) => {
        if (selectedIngredient) {
            setIngredientAllocations(prev => ({
                ...prev,
                [selectedIngredient._id]: allocations
            }))
            setPickedTotals(prev => ({
                ...prev,
                [selectedIngredient._id]: totalPicked
            }))
        }
    }

    // Get total picked quantity for an ingredient
    // Uses pre-computed total from modal (handles item-family case_pack conversion)
    const getPickedQuantity = (ingredientId) => {
        if (pickedTotals[ingredientId] !== undefined) {
            return pickedTotals[ingredientId]
        }
        const allocations = ingredientAllocations[ingredientId] || {}
        return Object.values(allocations).reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
    }

    // Check if ingredient has any allocations
    const hasAllocations = (ingredientId) => {
        const allocations = ingredientAllocations[ingredientId] || {}
        return Object.keys(allocations).length > 0
    }

    // Build allocations array from ingredientAllocations state
    const buildAllocationsArray = () => {
        const allocations = []
        for (const [ingredientId, inventoryMap] of Object.entries(ingredientAllocations)) {
            for (const [inventoryItemId, quantity] of Object.entries(inventoryMap)) {
                if (parseFloat(quantity) > 0) {
                    allocations.push({
                        ingredient_id: parseInt(ingredientId),
                        inventory_item_id: parseInt(inventoryItemId),
                        quantity: parseFloat(quantity)
                    })
                }
            }
        }
        return allocations
    }

    // Build the start production payload
    const buildStartProductionPayload = () => {
        const payload = {
            target_quantity: parseFloat(outputQuantity),
            production_location_id: parseInt(selectedLocation),
            allocations: buildAllocationsArray()
        }
        if (outputLot.trim()) payload.lot_number = outputLot.trim()
        if (outputBbDate) payload.bb_date = outputBbDate
        return payload
    }

// Validate common fields for both modes
    const validateCommon = () => {
        if (!selectedLocation) {
            setSubmitError('Please select an output location')
            return false
        }
        if (outputQuantity <= 0) {
            setSubmitError('Output quantity must be greater than 0')
            return false
        }
        // Check if all ingredients have allocations
        const unallocatedIngredients = recipe?.ingredients?.filter(
            ing => !hasAllocations(ing._id)
        ) || []

        if (unallocatedIngredients.length > 0) {
            const names = unallocatedIngredients.map(
                ing => ing.ingredient_name || ing.stock_item_name || ing.item_family_name
            ).join(', ')
            setSubmitError(`Please pick inventory for all ingredients. Missing: ${names}`)
            return false
        }

        return true
    }

    // Validate before start production
    const validateStartProduction = () => {
        if (!validateCommon()) return false
        return true
    }

    const handleStartProduction = async () => {
        setSubmitError(null)
        setSuccessMessage(null)

        if (!validateStartProduction()) {
            return
        }

        await startProduction()
    }

    const startProduction = async () => {
        setStartingProduction(true)
        setSubmitError(null)

        try {
            const payload = buildStartProductionPayload()
            console.log('Starting production with payload:', payload)

            const result = await productionAPI.startProduction(id, payload)

            if (result?.success || result?.production_lot) {
                const productionLot = result.production_lot
                // Navigate to production detail page
                navigate(`/production/${productionLot._id}`)
            } else {
                setSubmitError('Failed to start production. Please try again.')
            }
        } catch (error) {
            console.error('Error starting production:', error)
            setSubmitError(formatErrorResponse(error) || 'Failed to start production')
        } finally {
            setStartingProduction(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes' + returnFilters)}>
                    Back to List
                </Button>
            </Container>
        )
    }

    if (!recipe) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Recipe not found</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes' + returnFilters)}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const outputProduct = recipe.output_stock_item_data

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Execute Recipe: {recipe.name}</h1>
                </Col>
            </Row>

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            {outputProduct?.image ? (
                                <img
                                    src={outputProduct.image}
                                    alt={outputProduct.description}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                        </Col>
                        <Col>
                            <h5 className="mb-1">Output Product</h5>
                            <p className="mb-0 text-muted">
                                {outputProduct?.description || 'No output product specified'}
                            </p>
                        </Col>
                        <Col md={5}>
                            <Form.Group controlId="outputQuantity">
                                <Form.Label>Output Quantity</Form.Label>
                                <div className="d-flex align-items-center gap-3">
                                    <Form.Range
                                        min={1}
                                        max={maxQuantity}
                                        step={1}
                                        value={outputQuantity}
                                        onChange={(e) => handleQuantityChange(e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        max={maxQuantity}
                                        value={outputQuantity}
                                        onChange={(e) => handleQuantityChange(e.target.value)}
                                        style={{ width: '120px', fontSize: '1.5rem', fontWeight: 'bold' }}
                                    />
                                </div>
                                <Form.Text className="text-muted">
                                    Max available: {maxQuantity}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <h4>Required Ingredients</h4>
            {recipe.ingredients && recipe.ingredients.length > 0 ? (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>GTIN</th>
                            <th>Ingredient</th>
                            <th>Recipe Qty</th>
                            <th>Required (incl. waste)</th>
                            <th style={{ width: '150px' }}>Pick</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recipe.ingredients.map((ingredient) => {
                            const isWeight = ingredient.input_mode === 'weight' && ingredient.weight_value
                            const requiredQty = parseFloat(calculateRequiredQuantity(ingredient))
                            const pickedQty = getPickedQuantity(ingredient._id)
                            const isPicked = hasAllocations(ingredient._id)
                            // For weight-mode, skip quantity-based comparison (handled in the modal)
                            const isComplete = isPicked && !isWeight && Math.abs(pickedQty - requiredQty) < 0.0001
                            const isOver = isPicked && !isWeight && pickedQty > requiredQty
                            const isShort = isPicked && !isWeight && pickedQty < requiredQty

                            const availableQty = ingredientAvailability[ingredient._id]
                            const hasNoStock = availableQty === 0

                            return (
                                <tr key={ingredient._id}>
                                    <td>
                                        {ingredient.item_family ? '' : <CopyableText value={ingredient.gtin} toastLabel="GTIN" />}
                                    </td>
                                    <td>
                                        {ingredient.ingredient_name || ingredient.stock_item_name || ingredient.item_family_name}
                                        {hasNoStock && (
                                            <Badge bg="danger" className="ms-2">No Stock</Badge>
                                        )}
                                    </td>
                                    <td>
                                        {ingredient.input_mode === 'weight' && ingredient.weight_value ? (
                                            <span>{ingredient.weight_value} {ingredient.weight_unit}
                                                {!ingredient.item_family && ingredient.weight_per_case
                                                    ? <span> (<QuantityDisplay casePack={ingredient.case_pack || 1} uom={ingredient.unit_of_measure || ''} value={parseFloat(ingredient.quantity)} />)</span>
                                                    : null}
                                            </span>
                                        ) : (
                                            <>
                                                <QuantityDisplay casePack={ingredient.case_pack || 1} uom={ingredient.unit_of_measure || ''} value={parseFloat(ingredient.quantity)} />
                                                {(!ingredient.case_pack || ingredient.case_pack <= 1) && !isSuperUnit(ingredient.unit_of_measure) && (
                                                    <span> {ingredient.case_pack === null ? 'Items' : ingredient.unit_of_measure}</span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <strong>
                                            {ingredient.input_mode === 'weight' && ingredient.weight_value ? (
                                                <span>{calculateRequiredWeight(ingredient)} {ingredient.weight_unit}
                                                    {!ingredient.item_family && ingredient.weight_per_case
                                                        ? <span> (<QuantityDisplay casePack={ingredient.case_pack || 1} uom={ingredient.unit_of_measure || ''} value={parseFloat(calculateRequiredWeight(ingredient)) / ingredient.weight_per_case} />)</span>
                                                        : null}
                                                </span>
                                            ) : (
                                                <>
                                                    <QuantityDisplay casePack={ingredient.case_pack || 1} uom={ingredient.unit_of_measure || ''} value={parseFloat(calculateRequiredQuantity(ingredient))} />
                                                    {(!ingredient.case_pack || ingredient.case_pack <= 1) && !isSuperUnit(ingredient.unit_of_measure) && (
                                                        <span> {ingredient.case_pack === null ? 'Items' : ingredient.unit_of_measure}</span>
                                                    )}
                                                </>
                                            )}
                                        </strong>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                variant={isPicked ? 'success' : 'outline-primary'}
                                                size="sm"
                                                onClick={() => openPickModal(ingredient)}
                                            >
                                                {isPicked ? 'Edit' : 'Pick'}
                                            </Button>
                                            {isPicked && (
                                                <>
                                                    {isWeight ? (
                                                        <Badge bg="success">Picked</Badge>
                                                    ) : (
                                                        <>
                                                            {isComplete && (
                                                                <Badge bg="success">
                                                                    {pickedQty.toFixed(2)}
                                                                </Badge>
                                                            )}
                                                            {isOver && (
                                                                <Badge bg="warning" text="dark">
                                                                    {pickedQty.toFixed(2)} (+)
                                                                </Badge>
                                                            )}
                                                            {isShort && (
                                                                <Badge bg="danger">
                                                                    {pickedQty.toFixed(2)} (-)
                                                                </Badge>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            ) : (
                <Alert variant="info">No ingredients defined for this recipe.</Alert>
            )}

            <Card className="mb-4 mt-4">
                <Card.Header>
                    <h5 className="mb-0">Output Location</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="warehouse">
                                <Form.Label>Warehouse</Form.Label>
                                <Form.Select
                                    value={selectedWarehouse}
                                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                                >
                                    <option value="">Select Warehouse...</option>
                                    {warehouses.map(wh => (
                                        <option key={wh._id} value={wh._id}>
                                            {wh.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="location">
                                <Form.Label>Location</Form.Label>
                                <Form.Select
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    disabled={!selectedWarehouse || loadingLocations}
                                >
                                    <option value="">
                                        {loadingLocations ? 'Loading...' : 'Select Location...'}
                                    </option>
                                    {locations.map(loc => (
                                        <option key={loc._id} value={loc._id}>
                                            {loc.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Product Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="outputLot">
                                <Form.Label>LOT Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Auto-generated if empty"
                                    value={outputLot}
                                    onChange={(e) => setOutputLot(e.target.value)}
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="outputBbDate">
                                <Form.Label>Best Before Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={outputBbDate}
                                    onChange={(e) => setOutputBbDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Pick Inventory Modal */}
            <PickInventoryModal
                show={showPickModal}
                onHide={() => {
                    setShowPickModal(false)
                    setSelectedIngredient(null)
                }}
                ingredient={selectedIngredient}
                requiredQuantity={selectedIngredient ? parseFloat(calculateRequiredQuantity(selectedIngredient)) : 0}
                requiredWeight={selectedIngredient?.input_mode === 'weight' && selectedIngredient?.weight_value
                    ? parseFloat(calculateRequiredWeight(selectedIngredient))
                    : null}
                theoreticalQuantity={selectedIngredient ? parseFloat(calculateTheoreticalQuantity(selectedIngredient)) : 0}
                theoreticalWeight={selectedIngredient?.input_mode === 'weight' && selectedIngredient?.weight_value
                    ? parseFloat(calculateTheoreticalWeight(selectedIngredient))
                    : null}
                weightUnit={selectedIngredient?.weight_unit || null}
                recipeId={id}
                onConfirm={handleAllocationConfirm}
                existingAllocations={selectedIngredient ? ingredientAllocations[selectedIngredient._id] : null}
            />

            {submitError && (
                <Alert variant="danger" dismissible onClose={() => setSubmitError(null)} className="mt-4 mb-0">
                    {submitError}
                </Alert>
            )}

            <div className="mt-4 d-flex gap-2">
                <Button
                    variant="success"
                    onClick={handleStartProduction}
                    disabled={startingProduction || !isFormComplete}
                >
                    {startingProduction ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                            />
                            Starting...
                        </>
                    ) : (
                        'Start Production'
                    )}
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate('/recipes' + returnFilters)}
                    disabled={startingProduction}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>


        </Container>
    )
}

export default RecipeExecuteScreen
